import { useState } from 'react'
import { Button, Dialog, Field, NativeSelect, Portal, Stack, Text } from '@chakra-ui/react'
import type { ApiError, RejectDemandPayload, RejectionReason } from '../types'
import { Textarea } from '@chakra-ui/react'

interface RejectDemandDialogProps {
  open: boolean
  isSubmitting?: boolean
  error?: ApiError | null
  onOpenChange: (open: boolean) => void
  onSubmit: (payload: RejectDemandPayload) => void
}

const REJECTION_REASONS: Array<{
  value: RejectionReason
  label: string
}> = [
  { value: 'DISTANCE', label: 'Bedarf ist zu weit entfernt' },
  { value: 'NO_CAPACITY', label: 'Zum gewünschten Zeitpunkt nicht verfügbar' },
  { value: 'NO_FIT', label: 'Wirtschaftlich nicht interessant' },
  { value: 'OTHER', label: 'Sonstiges' },
]

export function RejectDemandDialog({
  open,
  isSubmitting = false,
  error,
  onOpenChange,
  onSubmit,
}: RejectDemandDialogProps) {
  const [reason, setReason] = useState<RejectionReason | ''>('')
  const [otherReason, setOtherReason] = useState('')

  const reasonMissing = !reason
  const otherReasonMissing = reason === 'OTHER' && !otherReason.trim()

  function handleSubmit() {
    if (!reason) return

    if (reason === 'OTHER' && !otherReason.trim()) {
      return
    }

    onSubmit({
      reason,
      details: reason === 'OTHER' ? otherReason.trim() : undefined,
    })
  }

  return (
    <Dialog.Root open={open} onOpenChange={details => onOpenChange(details.open)}>
      <Portal>
        <Dialog.Backdrop />

        <Dialog.Positioner>
          <Dialog.Content maxW="420px">
            <Dialog.Header>
              <Dialog.Title>Angebot ablehnen</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <Stack gap={6}>
                <Text color="fg.muted">
                  Bitte dokumentieren Sie den Grund für Ihre Ablehnung. Ihre Rückmeldung hilft
                  Union Investment, zukünftige Ausschreibungen besser einzuordnen.
                </Text>

                <Field.Root
                  required
                  invalid={reasonMissing || Boolean(error?.fieldErrors?.reason)}
                >
                  <Field.Label>Ablehnungsgrund</Field.Label>

                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={reason}
                      onChange={event => setReason(event.target.value as RejectionReason)}
                    >
                      <option value="">Bitte auswählen</option>
                      {REJECTION_REASONS.map(item => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>

                  <Field.ErrorText>
                    {error?.fieldErrors?.reason ?? 'Bitte wählen Sie einen Ablehnungsgrund aus.'}
                  </Field.ErrorText>
                </Field.Root>
                {reason === 'OTHER' && (
                  <Field.Root required invalid={otherReasonMissing}>
                    <Field.Label>Sonstiger Grund</Field.Label>

                    <Textarea
                      resize="none"
                      maxLength={50}
                      value={otherReason}
                      onChange={event => setOtherReason(event.target.value)}
                    />

                    <Field.HelperText>Max. 50 Zeichen</Field.HelperText>

                    <Field.ErrorText>Bitte geben Sie einen Grund an.</Field.ErrorText>
                  </Field.Root>
                )}
              </Stack>
            </Dialog.Body>

            <Dialog.Footer>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>

              <Button
                colorPalette="red"
                disabled={reasonMissing || otherReasonMissing}
                loading={isSubmitting}
                onClick={handleSubmit}
              >
                Angebot ablehnen
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
